import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { User, Package, Car, LogOut, Lock, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../App";
import MyGarage from "../components/MyGarage";
import VINSearch from "../components/VINSearch";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const ProfilePage = () => {
  const { user, logout, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Security settings state
  const [accountSettings, setAccountSettings] = useState(null);
  const [securityLoading, setSecurityLoading] = useState(true);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setOrders(response.data);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [token]);

  useEffect(() => {
    const fetchAccountSettings = async () => {
      try {
        const response = await axios.get(`${API}/account/settings`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setAccountSettings(response.data);
      } catch (error) {
        console.error("Error fetching account settings:", error);
      } finally {
        setSecurityLoading(false);
      }
    };
    if (token) {
      fetchAccountSettings();
    }
  }, [token]);

  const handleLogout = async () => {
    await logout();
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmNewPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(`${API}/account/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleToggle2FA = async (enabled) => {
    setTwoFactorLoading(true);
    try {
      const response = await axios.put(`${API}/account/settings/2fa`, {
        enabled
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAccountSettings(prev => ({ ...prev, two_factor_enabled: enabled }));
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update 2FA settings");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "text-green-600 bg-green-100";
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      case "shipped":
        return "text-blue-600 bg-blue-100";
      case "delivered":
        return "text-green-600 bg-green-100";
      case "cancelled":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 animate-fade-in" data-testid="profile-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border-2 border-gray-200" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
            )}
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 uppercase tracking-tight" data-testid="user-name">
                {user?.name}
              </h1>
              <p className="text-gray-500" data-testid="user-email">{user?.email}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="bg-white border border-gray-200 p-1 mb-8">
            <TabsTrigger value="orders" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="orders-tab">
              <Package className="w-4 h-4 mr-2" />
              My Orders
            </TabsTrigger>
            <TabsTrigger value="garage" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="garage-tab">
              <Car className="w-4 h-4 mr-2" />
              My Garage
            </TabsTrigger>
            <TabsTrigger value="account" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="account-tab">
              <User className="w-4 h-4 mr-2" />
              Account
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders" data-testid="orders-content">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white h-32 animate-pulse rounded-lg border border-gray-200"></div>
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl text-gray-900 mb-2">No orders yet</h3>
                <p className="text-gray-500 mb-6">Start shopping to see your orders here</p>
                <Link to="/shop">
                  <Button className="bg-primary hover:bg-primary/90">Browse Products</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div
                    key={order.order_id}
                    className="bg-white border border-gray-200 p-6 rounded-lg"
                    data-testid={`order-${order.order_id}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                      <div>
                        <p className="text-gray-900 font-medium">Order #{order.order_id}</p>
                        <p className="text-gray-500 text-sm">
                          {new Date(order.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                        <p className="text-gray-900 font-bold text-lg mt-1">${order.total.toFixed(2)}</p>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-500 text-sm mb-2">Items:</p>
                      <div className="space-y-2">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-gray-900">{item.name} x{item.quantity}</span>
                            <span className="text-gray-600">${(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Garage Tab */}
          <TabsContent value="garage" data-testid="garage-content">
            <div className="grid lg:grid-cols-2 gap-6">
              <VINSearch />
              <MyGarage />
            </div>
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account" data-testid="account-content">
            <div className="max-w-lg space-y-6">
              {/* Account Information */}
              <div className="bg-white border border-gray-200 p-6 rounded-lg">
                <h3 className="text-gray-900 font-heading text-lg uppercase mb-4">Account Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-gray-500 text-sm">Name</label>
                    <p className="text-gray-900">{user?.name}</p>
                  </div>
                  <div>
                    <label className="text-gray-500 text-sm">Email</label>
                    <p className="text-gray-900">{user?.email}</p>
                  </div>
                  <div>
                    <label className="text-gray-500 text-sm">Account Type</label>
                    <p className="text-gray-900 capitalize">{user?.role || 'Customer'}</p>
                  </div>
                </div>
              </div>

              {/* Security Settings */}
              <div className="bg-white border border-gray-200 p-6 rounded-lg">
                <div className="flex items-center gap-2 mb-6">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  <h3 className="text-gray-900 font-heading text-lg uppercase">Security Settings</h3>
                </div>

                {securityLoading ? (
                  <div className="space-y-4">
                    <div className="h-10 bg-gray-100 animate-pulse rounded"></div>
                    <div className="h-10 bg-gray-100 animate-pulse rounded"></div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Two-Factor Authentication */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid="2fa-toggle-section">
                      <div>
                        <p className="font-medium text-gray-900">Two-Factor Authentication</p>
                        <p className="text-sm text-gray-500">
                          {accountSettings?.two_factor_enabled 
                            ? "Enabled - Extra security layer active" 
                            : "Add an extra layer of security to your account"}
                        </p>
                      </div>
                      <Switch
                        checked={accountSettings?.two_factor_enabled || false}
                        onCheckedChange={handleToggle2FA}
                        disabled={twoFactorLoading || !accountSettings?.has_password}
                        data-testid="2fa-toggle"
                      />
                    </div>
                    {!accountSettings?.has_password && (
                      <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                        2FA is not available for Google-authenticated accounts. Set a password to enable this feature.
                      </p>
                    )}

                    {/* Change Password */}
                    {accountSettings?.has_password && (
                      <div className="border-t border-gray-200 pt-6" data-testid="password-change-section">
                        <div className="flex items-center gap-2 mb-4">
                          <Lock className="w-4 h-4 text-gray-600" />
                          <p className="font-medium text-gray-900">Change Password</p>
                        </div>
                        <form onSubmit={handlePasswordChange} className="space-y-4">
                          <div>
                            <Label htmlFor="current-password" className="text-gray-600">Current Password</Label>
                            <div className="relative mt-1">
                              <Input
                                id="current-password"
                                type={showCurrentPassword ? "text" : "password"}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                className="pr-10"
                                required
                                data-testid="current-password-input"
                              />
                              <button
                                type="button"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="new-password" className="text-gray-600">New Password</Label>
                            <div className="relative mt-1">
                              <Input
                                id="new-password"
                                type={showNewPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="pr-10"
                                required
                                minLength={6}
                                data-testid="new-password-input"
                              />
                              <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="confirm-new-password" className="text-gray-600">Confirm New Password</Label>
                            <Input
                              id="confirm-new-password"
                              type={showNewPassword ? "text" : "password"}
                              value={confirmNewPassword}
                              onChange={(e) => setConfirmNewPassword(e.target.value)}
                              className="mt-1"
                              required
                              data-testid="confirm-new-password-input"
                            />
                          </div>
                          <Button
                            type="submit"
                            disabled={passwordLoading}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="change-password-btn"
                          >
                            {passwordLoading ? "Changing..." : "Change Password"}
                          </Button>
                        </form>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-secondary text-secondary hover:bg-secondary hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfilePage;
